import { DealStatuses, EmployeePermissionsType } from '@core/classes/SharedEnums';
import { EmployeeDefaultsClass, SaturnPermissions } from '@core/classes/AccessEmployeeClass';
import { BitFlagHelper } from '@core/helpers/enum-helper';
import EmployeePermissionsEnumsHelper from '@core/_enums/UserPermissionsEnumsHelper';
import settings from 'settings';

// Helper for employee access/ Accounts / users.
export interface IEmployeeAccess {
	StoreCodes: string[];
}

export interface IDYWEmployeeInfo {
	UserId: string;
	Username: string;
}

export class UserPayload {
	id: string = null;
	EmployeeId: string = null;
	EmployeeName: string = null;
	EmployeeLogin: string = null;
	EmployeeEmail: string = null;
	EmployeeAccess: IEmployeeAccess = null;
	Position: string = null;
	PositionCode: string = null;
	EmployeeDefaults: EmployeeDefaultsClass = null;
	EmployeeCode: string = null;
	EmployeeTheme: string = null;
	AllowDealActivation: boolean = false;
	DisableRecording: boolean = false;
	AllowBrowser: boolean = false;
	AllowRemoteDealMeetings: boolean = false;
	IgnoreDepartmentRestriction: boolean = false;
	ExternalEmployeeInfo: IDYWEmployeeInfo = null;
	Permissions: EmployeePermissionsType[] = [];

	constructor(partial?: Partial<UserPayload>) {
		if (partial) {
			Object.assign(this, partial);
			this.EmployeeDefaults = partial?.EmployeeDefaults ? new EmployeeDefaultsClass(partial?.EmployeeDefaults) : null;
		}
	}

	/**
	 * Determines if a user is an admin.
	 *
	 * @returns True if the user is an admin; otherwise, false.
	 */
	get isAdmin(): boolean {
		return settings.permissions.isAdmin(this.PositionCode);
	}

	/**
	 * Converts the user's checklist permissions into a combined bit flag value
	 *
	 * @returns The checklist permission flags for the user.
	 */
	getChecklistPermissionFlags(): SaturnPermissions {
		let combinedPermissions = SaturnPermissions.None;

		// Merging all the permissions together into a single value
		EmployeePermissionsEnumsHelper.getChecklistPermissions()?.forEach(item => {
			if (this.Permissions.includes(item.value) && !!item.bitFlagValue) {
				combinedPermissions = BitFlagHelper.addFlag(combinedPermissions, item.bitFlagValue);
			}
		});

		if (this.isAdmin) BitFlagHelper.addFlag(combinedPermissions, SaturnPermissions.Admin);

		return combinedPermissions;
	}

	/**
	 * Determines if a user has access to the sales department.
	 *
	 * @returns True if the user has access to the sales department; otherwise, false.
	 */
	get hasSalesDepartmentAccess(): boolean {
		return this.isAdmin || this.IgnoreDepartmentRestriction || settings.permissions.isSalesPosition(this.PositionCode);
	}

	/**
	 * Determines if a user has access to the finance department.
	 *
	 * @returns True if the user has access to the finance department; otherwise, false.
	 */
	get hasFinanceDepartmentAccess(): boolean {
		return this.isAdmin || this.IgnoreDepartmentRestriction || settings.permissions.isFinancePosition(this.PositionCode);
	}

	/**
	 * Determines if a user has access to the accounting department.
	 *
	 * @returns True if the user has access to the accounting department; otherwise, false.
	 */
	get hasAccountingDepartmentAccess(): boolean {
		return this.isAdmin || this.IgnoreDepartmentRestriction || settings.permissions.isAccountingPosition(this.PositionCode);
	}

	/**
	 * Determines if a user has access to the management department.
	 *
	 * @returns True if the user has access to the management department; otherwise, false.
	 */
	get hasManagementDepartmentAccess(): boolean {
		return this.isAdmin || this.IgnoreDepartmentRestriction || settings.permissions.isManagementPosition(this.PositionCode);
	}

	/**
	 * Determines if a user has access to the service department.
	 *
	 * @returns True if the user has access to the service department; otherwise, false.
	 */
	get hasServiceDepartmentAccess(): boolean {
		return this.isAdmin || this.IgnoreDepartmentRestriction || settings.permissions.isServicePosition(this.PositionCode);
	}

	/**
	 * Determines if a user has headquarter level access.
	 *
	 * @returns True if the user has headquarter level access; otherwise, false.
	 */
	get hasHeadquarterDepartmentAccess(): boolean {
		return this.isAdmin || this.IgnoreDepartmentRestriction || settings.permissions.isHeadquarterPosition(this.PositionCode);
	}

	/**
	 * Determines if a user has access to a given permission.
	 *
	 * @param permission The permission to check for access.
	 * @returns True if the user has access to the permission; otherwise, false.
	 */
	private hasPermission(permission: EmployeePermissionsType): boolean {
		return this.Permissions.includes(permission);
	}

	/**
	 * Determines if a user has access to a given permission.
	 *
	 * @param permission The permission to check for access.
	 * @returns True if the user has access to the permission; otherwise, false.
	 */
	hasAccessToPermission(permission: EmployeePermissionsType): boolean {
		return this.isAdmin || this.hasPermission(permission);
	}

	/**
	 * Determines whether the user has access to edit a deal based on its status and the user's access level.
	 *
	 * @param dealStatus The status of the deal.
	 * @param userAccess The access level of the user.
	 * @returns True if the user has access to edit the deal; otherwise, false.
	 */
	hasAccessToEditDeal(dealStatus: DealStatuses): boolean {
		if (this.isAdmin) return true;

		// A user has access if the department is correct AND they can make changes to that department
		if (dealStatus <= DealStatuses.SalesFinished) {
			return this.hasSalesDepartmentAccess && this.hasPermission(EmployeePermissionsType.SalesPersonChecklist);
		} else if (dealStatus <= DealStatuses.SalesManagerFinished) {
			return this.hasSalesDepartmentAccess && this.hasPermission(EmployeePermissionsType.SalesManagerChecklist);
		} else if (dealStatus <= DealStatuses.FiInProcess) {
			return this.hasFinanceDepartmentAccess && this.hasPermission(EmployeePermissionsType.FIManagerChecklist);
		} else if (dealStatus <= DealStatuses.FiAdminInProcess) {
			return this.hasAccountingDepartmentAccess && this.hasPermission(EmployeePermissionsType.FIAdminChecklist);
		} else if (dealStatus <= DealStatuses.AccountingInProcess) {
			return this.hasAccountingDepartmentAccess && this.hasPermission(EmployeePermissionsType.CostingClerkChecklist);
		} else if (dealStatus >= DealStatuses.AccountingFinished) {
			return false;
		}

		// Default: user does not have access
		return false;
	}
}
